import React from 'react'
import { Box, Chip, Divider, Typography } from '@mui/material'
import ForkRightIcon from '@mui/icons-material/ForkRight'
import SettingsRow from '../../../layout/settingsMenu/SettingsRow'
import SectionTitle from '../../../layout/settingsMenu/SectionTitle'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { forceExitLabel } from '../../../common/utils/forceExit'

interface TradeParamsReadViewProps {
  alert: Alert
}

const TradeParamsReadView: React.FC<TradeParamsReadViewProps> = ({ alert }) => {
  const { usermeta } = useApplicationSuiteContext()
  const connectedAccount = usermeta.metaapi?.accounts?.find(
    (acc: any) => acc.accountId === alert.trading?.accountId
  )

  console.log(alert, connectedAccount)

  const options = alert.trading?.options
  if (!options) return null

  const { analyzeTrades } = options
  const generateTrades = options.generateTrades as any

  const exitStrategyRaw = generateTrades.exitStrategy
  const exitStrategyLabel =
    exitStrategyRaw === 'peak_valley'
      ? 'Fractal'
      : exitStrategyRaw === 'high_low'
        ? 'High/Low'
        : exitStrategyRaw[0].toUpperCase() + exitStrategyRaw.slice(1)

  const riskReward = (
    <Box display="inline-flex" alignItems="center" gap={0.5} flexWrap="wrap">
      {generateTrades.tradeRatios.map((ratio: any, idx: number) => (
        <Chip
          key={idx}
          size="small"
          variant="outlined"
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>
                {ratio.risk}/{ratio.reward}
              </span>
              {ratio.conditions?.length > 0 && (
                <>
                  <span>·</span>
                  <Box display="flex" alignItems="center">
                    <span>{ratio.conditions.length}</span>
                    <ForkRightIcon sx={{ fontSize: 14 }} />
                  </Box>
                </>
              )}
            </Box>
          }
        />
      ))}
    </Box>
  )

  const forceExit = forceExitLabel(analyzeTrades)
  const maxRisk = analyzeTrades.maxRisk ? `${(analyzeTrades.maxRisk * 100).toFixed(0)}%` : '∞'
  const riskPerTrade = `${(analyzeTrades.riskPerTrade * 100).toFixed(2)}%`
  const maxOpenPositions = analyzeTrades.tradeLimit ? `${analyzeTrades.tradeLimit}` : '∞'
  const dailyTradeLimit = analyzeTrades.dailyTradeLimit ? `${analyzeTrades.dailyTradeLimit}` : '∞'

  return (
    <Box>
      <SectionTitle title="Account" />
      <SettingsRow title="Max Risk" data={maxRisk} />
      <SettingsRow title="Risk Per Trade" data={riskPerTrade} />
      <SettingsRow title="Max Open Positions" data={maxOpenPositions} />
      <SettingsRow title="Daily Trade Limit" data={dailyTradeLimit} />
      {analyzeTrades.allowHedging && <SettingsRow title="Hedging" data="Enabled" />}
      {forceExit && <SettingsRow title="Close After" data={forceExit} />}

      <Divider sx={{ my: 1 }} />

      <SectionTitle title="Stop Loss" />
      <SettingsRow title="Strategy" data={exitStrategyLabel} />
      <SettingsRow title="Risk / Reward" data={riskReward} />

      {exitStrategyRaw === 'atr' && (
        <>
          <SettingsRow title="ATR Period" data={`${generateTrades.atrPeriod}`} />
          <SettingsRow
            title="ATR Chart"
            data={`${generateTrades.atrChartPeriod} ${generateTrades.atrChartTimeframe}`}
          />
        </>
      )}

      {exitStrategyRaw === 'peak_valley' && (
        <>
          <SettingsRow title="Fractal Count" data={`${generateTrades.fractalCount}`} />
          <SettingsRow title="Max Lookback" data={`${generateTrades.maxPeakValleyLookback}`} />
          <SettingsRow title="Price Offset" data={`${generateTrades.priceOffset}`} />
        </>
      )}

      {exitStrategyRaw === 'high_low' && (
        <>
          <SettingsRow title="Candle Lookback" data={`${generateTrades.maxLookback}`} />
          <SettingsRow title="Price Offset" data={`${generateTrades.priceOffset}`} />
        </>
      )}

      {exitStrategyRaw === 'incremental' && (
        <>
          {generateTrades.incrementalStart != null && (
            <SettingsRow title="Start" data={`${generateTrades.incrementalStart}`} />
          )}
          {generateTrades.incrementalStop != null && (
            <SettingsRow title="Stop" data={`${generateTrades.incrementalStop}`} />
          )}
          {generateTrades.incrementalStep != null && (
            <SettingsRow title="Step" data={`${generateTrades.incrementalStep}`} />
          )}
        </>
      )}

      {connectedAccount && (
        <>
          <Divider sx={{ my: 1 }} />
          <SectionTitle title="Trading Account" />
          <SettingsRow title="Account" data={connectedAccount.name} />
          <SettingsRow title="Server" data={connectedAccount.server} />
          {alert.trading?.broker?.symbol && (
            <SettingsRow title="Symbol" data={alert.trading.broker.symbol} />
          )}
        </>
      )}

      {alert.trading?.exitTradingPlanId && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            Exit pattern configured
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default TradeParamsReadView
